import {
  useAnalyticsCartItems,
  useCartAndDeliveryDatesDataContext,
} from '@app/data/clients/cartCheckoutClient';
import { AnalyticsPageType } from '@redbubble/next-analytics/gtm';
import { type RSCheckoutLocationKey } from '@redbubble/next-provider/rudderstack';
import { useEffect } from 'react';
import { StripeExpress } from './StripeExpress';
import {
  ErrorMessage,
  PageSource,
  PageSourceType,
  StripeExpressAnalyticsPageType,
} from './types';
import { useStripeShippingAddressChangeHandler } from './useStripeShippingAddressChangeHandler';
import { useStripeShippingPreferenceChangeHandler } from './useStripeShippingPreferenceChangeHandler';
import { mapMonolithCartToStripeCart } from './utils';

export type MonolithCartStripeContainerProps = {
  analyticsPageType?: StripeExpressAnalyticsPageType;
  pageSource?: PageSourceType;
  onPayError: (message: ErrorMessage) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onLaunch: () => void;
  rsCheckoutLocation: RSCheckoutLocationKey;
};

export const MonolithCartStripeContainer = ({
  analyticsPageType = AnalyticsPageType.ProductDetailsPage,
  pageSource = PageSource.cartTray,
  onPayError,
  onCancel,
  onConfirm,
  onLaunch,
  rsCheckoutLocation,
}: MonolithCartStripeContainerProps) => {
  const { cart, cartLoading, hasStartedInitialLoad } =
    useCartAndDeliveryDatesDataContext();
  const analyticsItems = useAnalyticsCartItems();

  const stripeCart = cart && mapMonolithCartToStripeCart(cart);

  const { handleShippingAddressChange } =
    useStripeShippingAddressChangeHandler();
  const { handleShippingPreferenceChange } =
    useStripeShippingPreferenceChangeHandler();

  // XXX(nc) GooglePay backdrop overlay has a low-ish z-index (9,999,999) compared to Paypal (2,147,483,647)
  // we need a minimum of 1,000,000,000 for it to be above Cart Tray (which is set via design system Drawer Component).
  // I've explored lowering the z-index of the Cart Tray < 9,999,999 but that presents another issue
  // Our header component has a z-index of 999,999,998 and if Cart Tray has a lower z-index, the header will not be blocked :(
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new MutationObserver(() => {
      const overlay = document.querySelector('[data-testid="overlay"]');
      if (overlay instanceof HTMLElement) {
        overlay.style.zIndex = '1000000001';
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  if (!stripeCart) {
    return null;
  }

  return (
    <StripeExpress
      analyticsPageType={analyticsPageType}
      rsCheckoutLocation={rsCheckoutLocation}
      pageSource={pageSource}
      cart={stripeCart}
      cartLoading={!cart && (cartLoading || !hasStartedInitialLoad)}
      analyticsItems={analyticsItems}
      onPayError={onPayError}
      onShippingPreferenceChange={handleShippingPreferenceChange}
      onShippingAddressChange={handleShippingAddressChange}
      onCancel={onCancel}
      onConfirm={onConfirm}
      onLaunch={onLaunch}
    />
  );
};

export default MonolithCartStripeContainer;
